import React, { memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Spinner from "../../Spinner";
import PostExertionResultRow from "../rows/PostExertionResultRow";
import { useMeasurementResults } from "../../../hooks/results/useMeasurementResults";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../../hooks/results/useInfiniteResultsPageSize";

export default memo(function PostExertionResults({ db, uid }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);

  const { rows, loading, loadingMore, hasMore, error } = useMeasurementResults(
    db,
    uid,
    {
      collectionName: "RecordingPostExertion",
      pageSize,
      orderField: "createdAt",
    },
  );

  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey: `${uid || ""}:RecordingPostExertion`,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useCallback(
    (row) => {
      const docid = row?.id;
      if (!docid) return;

      const params = new URLSearchParams({
        variant: "RecordingPostExertion",
        docid,
        context: "post-exertion",
        tab: "post-exertion",
      });

      navigate(`/result?${params.toString()}`);
    },
    [navigate],
  );

  if (!uid || loading) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="muted-text text-center space-y-1">
        {t("results.failedLoad", "Failed to load results.")}
        {error?.message ? <div>{error.message}</div> : null}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="muted-text">
        {t("results.emptyRecordPrompt", "No data yet")}
      </div>
    );
  }

  return (
    <div
      aria-label="post exertion results list"
      className="w-full h-full min-h-0 overflow-y-auto"
      onScroll={handleScroll}
    >
      {rows.map((row, idx) => (
        <PostExertionResultRow
          key={row.id ?? idx}
          time={row.time}
          date={row.date}
          rr={row.rr}
          rrMin={row.rrMin}
          rrMax={row.rrMax}
          rrQuality={row.rrQuality}
          onClick={() => onRowClick(row)}
        />
      ))}
      {loadingMore ? <Spinner size={24} className="py-4" /> : null}
    </div>
  );
});
